import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import LoginData from '../sign_in/types/LoginData';
import NewUserDto from '../sign_up/types/NewUserDto';
import * as api from './api';
import AuthState from './types/AuthState';
import User from './types/User';

const initialState: AuthState = {
	isAuth: false,
	isLoading: false,
	authChecked: false,
	user: null,
	authError: '',
};

export const login = createAsyncThunk('login', async (data: LoginData, { rejectWithValue }) => {
	try {
		const response = await api.login(data);
		const isSecure = window.location.protocol === 'https:';

		Cookies.set('accessToken', response.accessToken, {
			expires: 1 / 12,
			secure: isSecure,
			sameSite: 'strict',
		});

		Cookies.set('refreshToken', response.refreshToken, {
			expires: 30,
			secure: isSecure,
			sameSite: 'strict',
		});

		const user = await api.getAuthUser();
		return user;
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const getAuthUser = createAsyncThunk('getAuthUser', async (_, { rejectWithValue }) => {
	try {
		return await api.getAuthUser();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const register = createAsyncThunk(
	'register',
	async (data: NewUserDto, { rejectWithValue }) => {
		try {
			return await api.register(data);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const logout = createAsyncThunk<void>('logout', () => {
	Cookies.remove('accessToken');
	Cookies.remove('refreshToken');
});

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		resetAuthState: (state) => {
			state.isAuth = false;
			state.isLoading = false;
			state.authChecked = false;
			state.user = null;
			state.authError = '';
		},
		setIsLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		},
		finishAuthCheck: (state) => {
			state.authChecked = true;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(login.pending, (state) => {
				state.isLoading = true;
				state.authError = '';
			})
			.addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
				state.isLoading = false;
				state.isAuth = true;
				state.authChecked = true;
				state.user = action.payload;
				state.authError = '';
			})
			.addCase(login.rejected, (state, action) => {
				state.isLoading = false;
				state.isAuth = false;
				state.authChecked = true;
				state.user = null;
				state.authError = action.error.message || 'Login failed';
			})

			.addCase(getAuthUser.pending, (state) => {
				state.isLoading = true;
				state.authError = '';
			})
			.addCase(getAuthUser.fulfilled, (state, action: PayloadAction<User>) => {
				state.isLoading = false;
				state.isAuth = true;
				state.authChecked = true;
				state.user = action.payload;
				state.authError = '';
			})
			.addCase(getAuthUser.rejected, (state) => {
				state.isLoading = false;
				state.isAuth = false;
				state.authChecked = true;
				state.user = null;
				state.authError = 'Unauthorized';
			})

			.addCase(register.pending, (state) => {
				state.isLoading = true;
				state.authError = '';
			})
			.addCase(register.fulfilled, (state) => {
				state.isLoading = false;
				state.authError = '';
			})
			.addCase(register.rejected, (state, action) => {
				state.isLoading = false;
				state.authError = action.error.message || 'Something went wrong';
			})

			.addCase(logout.fulfilled, (state) => {
				state.isAuth = false;
				state.isLoading = false;
				state.authChecked = true;
				state.user = null;
				state.authError = '';
			});
	},
});

export const { resetAuthState, setIsLoading, finishAuthCheck } = authSlice.actions;
export default authSlice.reducer;
